export const BEHAVIORAL_QUESTION_GROUPS = {
  LATEST: {
    name: "Latest question set",
  },
  CUSTOM: {
    name: "Custom questions",
  },
  DEPRECATED: {
    name: "Legacy questions",
  },
};

export function snapshotSetupBehavioralQuestions(args) {
  const {
    minBehavioralQuestionsPerSnapshot,
    maxBehavioralQuestionsPerSnapshot,
    behavioralQuestionSamplingThreshold,
    selectedBehavioralQuestions,
    allAvailableBehavioralQuestions,
    allDeprecatedBehavioralQuestions,
    snapshotBranches,
    core4WorkflowsCount,
  } = args;

  return {
    behavioralQuestionSamplingThreshold,
    selectedBehavioralQuestions,
    allAvailableBehavioralQuestions,
    allDeprecatedBehavioralQuestions,
    behavioralQuestionSelections: {},
    snapshotBranches,
    core4WorkflowsCount,
    shadowing: args.shadowing,
    QUESTION_GROUPS: BEHAVIORAL_QUESTION_GROUPS,
    init() {
      // Remove when we fully deprecate v2 workflow questions
      if (this.shadowing) {
        this.allAvailableBehavioralQuestions = [
          ...this.allAvailableBehavioralQuestions,
          ...this.allDeprecatedBehavioralQuestions,
        ];
      }

      allAvailableBehavioralQuestions.forEach((behavioralQuestion) => {
        this.behavioralQuestionSelections[behavioralQuestion.id] =
          selectedBehavioralQuestions.includes(behavioralQuestion.id);
      });

      const form = this.$root.closest("form");

      if (form) {
        const workflowEvent = new CustomEvent("dx:input-init", {
          detail: {
            name: "behavioralQuestionSelections",
            values: this.selectedOptions(),
          },
        });
        form.dispatchEvent(workflowEvent);

        this.$watch("behavioralQuestionSelections", () =>
          this.publishValues(form),
        );
      }
    },
    publishValues(form) {
      const workflowEvent = new CustomEvent("dx:input-changed", {
        detail: {
          name: "behavioralQuestionSelections",
          values: this.selectedOptions(),
        },
      });

      form.dispatchEvent(workflowEvent);
    },
    get hasSnapshotBranches() {
      return snapshotBranches.length > 0;
    },
    get hasBranchWithMoreThanThreshold() {
      return snapshotBranches.some(
        (branch) => this.sampleSizeForBranch(branch) < 100,
      );
    },
    // Group questions based on their created_at date
    getQuestionGroup(question) {
      if (question.custom) {
        return "CUSTOM";
      } else if (!question.deleted_at) {
        return "LATEST";
      } else {
        return "DEPRECATED";
      }
    },

    // Get questions for a specific group
    getQuestionsForGroup(groupKey) {
      // Getting the questions for the group and removing duplicates by ID
      const questions = this.allAvailableBehavioralQuestions
        .filter((q) => this.getQuestionGroup(q) === groupKey)
        .filter(
          (q, index, self) => index === self.findIndex((t) => t.id === q.id),
        );

      // For deprecated questions, show selected ones based on initial state (not current reactive state)
      if (groupKey === "DEPRECATED" && !this.shadowing) {
        return questions.filter((q) =>
          selectedBehavioralQuestions.includes(q.id),
        );
      }

      return questions;
    },

    showQuestionForShadowingOnly(question) {
      return (
        this.shadowing &&
        this.getQuestionGroup(question) === "DEPRECATED" &&
        !this.isSelected(question.id)
      );
    },

    // Get all group keys that have questions
    getActiveGroups() {
      return Object.keys(BEHAVIORAL_QUESTION_GROUPS).filter(
        (key) => this.getQuestionsForGroup(key).length > 0,
      );
    },

    samplingExplanation() {
      return `Sampling is used to collect data for all questions while capping the number of questions each respondent is shown to a maximum of ${behavioralQuestionSamplingThreshold - core4WorkflowsCount}. Sampling rate refers to the percentage of respondents that will see each sampled question.`;
    },
    emitSaved() {
      const workflowsSavedEvent = new CustomEvent("dx:workflows-saved");

      document.dispatchEvent(workflowsSavedEvent);
    },
    sampleSizeForBranch(branch) {
      const branchWorkflows = allAvailableBehavioralQuestions.filter(
        (question) =>
          question.snapshot_branches.map((b) => b.id).includes(branch.id) &&
          this.isSelected(question.id),
      );

      // If there are no workflows for this branch, then all of them are shown
      if (branchWorkflows.length === 0) return 100;

      // Be sure to include the core4 workflows in the total
      if (
        branchWorkflows.length <
        this.behavioralQuestionSamplingThreshold - core4WorkflowsCount
      )
        return 100;

      return (
        ((this.behavioralQuestionSamplingThreshold - core4WorkflowsCount) /
          branchWorkflows.length) *
        100
      ).toFixed(0);
    },
    toggleBehavioralQuestion(id) {
      if (this.behavioralQuestionSelections[id]) {
        this.behavioralQuestionSelections[id] = false;

        return;
      }

      this.behavioralQuestionSelections[id] = true;
    },
    isSelected(id) {
      return this.behavioralQuestionSelections[id];
    },
    isDisabled(id) {
      return !this.behavioralQuestionSelections[id] && this.slotsLeft() <= 0;
    },
    selectedOptions() {
      const selectedItems = [];

      for (const id in this.behavioralQuestionSelections) {
        if (this.behavioralQuestionSelections[id]) {
          selectedItems.push(id);
        }
      }

      return selectedItems;
    },
    slotsUsed() {
      return this.selectedOptions().length;
    },
    slotsLeft() {
      return this.maxSlots - this.slotsUsed();
    },
    get isConfigValid() {
      const slotsUsed = this.slotsUsed();

      if (slotsUsed < this.minSlots || slotsUsed > this.maxSlots) return false;

      return true;
    },
    get maxSlots() {
      return maxBehavioralQuestionsPerSnapshot;
    },
    get minSlots() {
      return minBehavioralQuestionsPerSnapshot;
    },
  };
}
